<template>
  <form-layout
    :v-if="jobRequest"
    :is-loading="isLoading"
    :title="jobRequestTitle"
    bottom-bar-stick-bottom
    @on-dismiss="dismiss"
  >
    <FieldViewJobRequestPanelProvision
      v-if="!isLoading && requestJobIds.length > 0 && parentRouteName === 'field-view-next'"
      :job-ids="requestJobIds"
    />

    <template #breadcrumbs>
      <vs-text>{{ displayedRequestId }}</vs-text>
    </template>

    <template #top-sheet>
      <MultiJobsTripMap
        v-if="!isLoading && requestJobIds.length > 0 && parentRouteName !== 'field-view-next'"
        :job-ids="requestJobIds"
        :map-link="mapLink"
        class="mb-4"
      />
    </template>

    <vs-heading type="overline" class="pb-4">Details</vs-heading>
    <job-request-details
      v-if="!isLoading && primaryJob"
      :job="primaryJob"
      :is-repeat-request="isRepeatRequest"
      data-test="job-request-details"
      class="mb-8"
    />
    <vs-heading type="overline" class="pb-4">Jobs</vs-heading>
    <job-request-trip-totals :jobs="requestJobs" :stops-enabled="stopsEnabled" class="mb-8" />

    <vs-wrapper>
      <vs-wrapper v-if="!isLoading && jobRequest">
        <job-request-job-card
          v-for="job in requestJobs"
          :key="job.number"
          :is-selected="isJobSelected(job.id)"
          :job-request="jobRequest"
          :job="job"
          :link-to="makeJobRoute(job.number)"
          :stops-enabled="stopsEnabled"
          @selected="handleSelectJob"
          @deselected="handleDeselectJob"
        />
      </vs-wrapper>
      <v-progress-circular
        v-else
        :size="64"
        :width="6"
        color="primary"
        indeterminate
        data-test="jobs-loading-spinner"
      />
    </vs-wrapper>
    <div class="mb-4" />
    <template #action-bar>
      <job-request-line-items v-if="isRateViewer" :job-ids="requestJobIds" class="mb-4" />

      <vs-wrapper v-if="selectedJobs.length">
        <v-row>
          <v-col>
            <vs-wrapper direction="horizontal">
              <vs-button
                :label="updateJobsLabel"
                full-width
                :type="isApprovalAvailable ? 'secondary' : 'primary'"
                data-test="update-jobs-button"
                @click="isBulkUpdateFormOpen = true"
              />
              <vs-button
                v-if="areAllJobsSelected"
                label="Deselect All"
                full-width
                type="secondary"
                data-test="deselect-all-button"
                @click="handleDeselectAll"
              />
              <vs-button
                v-else
                label="Select All"
                full-width
                type="secondary"
                data-test="select-all-button"
                @click="handleSelectAll"
              />
            </vs-wrapper>
          </v-col>
        </v-row>

        <v-row v-if="isApprovalAvailable">
          <v-col>
            <vs-button full-width :label="approvalButtonLabel" @click="doBulkApproval = true" />
          </v-col>
        </v-row>
      </vs-wrapper>

      <vs-button
        v-else
        label="Select All"
        full-width
        type="secondary"
        data-test="select-all-button"
        @click="handleSelectAll"
      />
    </template>

    <right-drawer :is-visible="isBulkUpdateFormOpen">
      <bulk-update-form
        :selected-jobs="selectedJobs"
        :allow-add-to-project="false"
        @dismiss="isBulkUpdateFormOpen = false"
        @updated="fetchJobs"
        @bottom-sheet-closed="isBulkUpdateBottomSheetOpen = false"
        @bottom-sheet-open="isBulkUpdateBottomSheetOpen = true"
      ></bulk-update-form>
    </right-drawer>

    <template #bottom-sheet>
      <vs-bottom-sheet :is-visible="isJobPanelVisible" full-height>
        <router-view @job-saved="fetchJobs" />
      </vs-bottom-sheet>
      <bulk-approve-form
        v-if="doBulkApproval"
        :selected-jobs="selectedJobs"
        @updated="handleBulkApprove"
        @dismiss="doBulkApproval = false"
      />
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '@/components/common/FormLayout.vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useGetJobRequestJobsQuery, useGetJobRequestQuery } from '@/api/jobs'
import { computed, ref, watch } from 'vue'
import JobRequestJobCard from '@/components/request/JobRequestJobCard.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import VsButton from '@/components/vision/VsButton.vue'
import RightDrawer from '@/components/common/RightDrawer.vue'
import BulkUpdateForm from '@/components/jobs/BulkUpdateForm.vue'
import JobRequestDetails from '@/components/request/JobRequestDetails.vue'
import { useStore } from 'vuex-composition-helpers'
import VsText from '@/components/vision/VsText.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { orderBy } from 'lodash'
import JobRequestLineItems from '@/components/request/JobRequestLineItems.vue'
import JobRequestTripTotals from '@/components/request/JobRequestTripTotals.vue'
import BulkApproveForm from '@/components/jobs/BulkApproveForm.vue'
import { ApprovalStatus, JobStatus } from '@/models/job'
import MultiJobsTripMap from '@/components/tickets/MultiJobsTripMap.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import FieldViewJobRequestPanelProvision from '@/components/field-view-next/FieldViewJobRequestPanelProvision.vue'
import { StopConfirmationRequirement } from '@/models/activity'

interface Props {
  id: string
  parentRouteName: string
  parentRouteParams?: Record<string, string>
}

const props = withDefaults(defineProps<Props>(), {
  parentRouteParams: () => ({}),
})

const store = useStore()
const route = useRoute()
const router = useRouter()
const queryClient = useQueryClient()

const isLoading = computed(() => isJobRequestLoading.value || areJobsLoading.value)

const mapLink = computed(() => {
  if (props.parentRouteName === 'field-view-next') return null
  return {
    name: 'fvnext-job-request',
    params: { requestId: props.id },
  }
})

const isRateViewer = computed(() => store.getters['permissions/isRateViewer'])

// request details
const primaryJob = computed(() =>
  requestJobs.value?.find((job) => job.id === jobRequest.value?.primaryJobId),
)
const primaryJobActivityId = computed(() => primaryJob.value?.resource_type_activity_id)

const getActivityById = computed(() => store.getters['activities/getById'])
const activity = computed(() => getActivityById.value(primaryJobActivityId.value))
const getByChildHubId = computed(() => store.getters['companies/getByChildHubId'])
const company = computed(() => getByChildHubId.value(primaryJob.value?.hub_id))
const displayedRequestId = computed(() => `Request · ${company.value?.shortCode ?? ''}`)
const jobRequestTitle = computed(() => {
  const activityText = isRepeatRequest.value ? (activity.value?.taskName ?? '') : 'Multi-Activity'
  return `${primaryJob.value?.resource_type_name ?? ''} · ${activityText}`
})
const stopsEnabled = computed(
  () => activity.value?.stopConfirmationRequirement === StopConfirmationRequirement.Required,
)
const isRepeatRequest = computed(() => jobRequest.value?.isRepeatTrip ?? false)

// bulk update drawer
const isBulkUpdateFormOpen = ref(false)
const isBulkUpdateBottomSheetOpen = ref(false)

// direct bulk approval

const canApproveJob = (hubId: string, accountId: string) =>
  store.getters['permissions/canApproveJob'](hubId, accountId) as boolean
const userCanApproveJob = computed(() => {
  return selectedJobs.value.every((j) => canApproveJob(j.hub_id, j.account_id))
})
const allSelectedJobsApprovable = computed(() =>
  selectedJobs.value.every(
    (j) => j.approval_status !== ApprovalStatus.APPROVED && j.status === JobStatus.COMPLETED,
  ),
)
const isApprovalAvailable = computed(
  () => selectedJobs.value.length > 0 && allSelectedJobsApprovable.value && userCanApproveJob.value,
)

const doBulkApproval = ref(false)
const approvalButtonLabel = computed(
  () => `Approve ${selectedJobs.value.length} job${selectedJobs.value.length === 1 ? '' : 's'}`,
)
async function handleBulkApprove() {
  await fetchJobs()
  doBulkApproval.value = false
}

// fetching job request and jobs
const { data: jobRequest, isLoading: isJobRequestLoading } = useGetJobRequestQuery(props.id || '')
const { data: jobRequestJobs, isLoading: areJobsLoading } = useGetJobRequestJobsQuery(
  props.id || '',
)
const requestJobs = computed(() => orderBy(jobRequestJobs.value ?? [], 'number', 'desc'))
const requestJobIds = computed(() =>
  requestJobs.value.map((job) => job.id).filter((jobId): jobId is string => Boolean(jobId)),
)
const fetchJobs = async () => {
  await queryClient.invalidateQueries(['job-request', 'jobsById', props.id])
}

// Job bottom-sheet
function makeJobRoute(jobNumber: number) {
  return {
    name: jobPanelUrl.value,
    params: { id: `${jobNumber}` },
  }
}
const ticketInfoPanel = 'ticket-info-panel'
const jobPanelUrl = computed(() =>
  route.name?.includes(ticketInfoPanel) ? route.name : `${route.name}-${ticketInfoPanel}`,
)
const isJobPanelVisible = computed(() => route.name?.includes(ticketInfoPanel) ?? false)

// Selecting jobs
const selectedJobIds = ref<string[]>([])
const areAllJobsSelected = computed(() => selectedJobIds.value.length === requestJobs.value.length)
const selectedJobs = computed(() =>
  requestJobs.value.filter((job) => selectedJobIds.value.includes(job.id)),
)

function isJobSelected(jobId: string) {
  return selectedJobIds.value.includes(jobId)
}
function handleSelectJob(selectedJobId: string) {
  selectedJobIds.value.push(selectedJobId)
}
function handleDeselectJob(deselectedJobId: string) {
  selectedJobIds.value = selectedJobIds.value.filter((jobId) => jobId !== deselectedJobId)
}

function handleSelectAll() {
  selectedJobIds.value = requestJobs.value.map((job) => job.id)
}
function handleDeselectAll() {
  selectedJobIds.value = []
}

// Labels
const updateJobsLabel = computed(() => {
  if (selectedJobIds.value.length === 1) return 'Update job'
  return `Update ${selectedJobIds.value.length} jobs`
})

function dismiss() {
  router.push({
    name: props.parentRouteName,
    params: props.parentRouteParams,
  })
}

// Select all jobs once they're available — immediate handles the cached case,
// and subsequent triggers handle the async-load case.
watch(
  requestJobs,
  (jobs) => {
    if (jobs.length) {
      selectedJobIds.value = jobs.map((job) => job.id)
    }
  },
  { immediate: true },
)
</script>
